/*
  A carrier's several options on one submission (S2BWB-276).

  A SHAPE carrier may quote one submission several times, once per limit and
  retention. Each option is its own quote row, and the API numbers a carrier's
  options (optionNumber, optionLimit, optionRetention) only when the carrier has two
  or more. Everything here leaves a quote without an option number exactly as it was:
  its label is the carrier name alone and its position does not move.
*/

const OPTION_FIELDS = ["optionNumber", "optionLimit", "optionRetention"];

const isAmount = (value) => value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value));

const round2 = (value) => Number(value.toFixed(2));

const UNITS = [
  [1, ""],
  [1e3, "K"],
  [1e6, "M"],
  [1e9, "B"],
];

/**
 * "$1M", "$2.5M", "$10K", "$500": a limit or retention, short enough for a header.
 * Rounded before the unit is settled, so 999,999 reads "$1M", not "$1000K".
 */
export function formatOptionAmount(value) {
  if (!isAmount(value)) return "";
  const amount = Number(value);
  const sign = amount < 0 ? "-" : "";
  const abs = Math.abs(amount);
  let index = 0;
  while (index < UNITS.length - 1 && abs >= UNITS[index + 1][0]) index += 1;
  let scaled = round2(abs / UNITS[index][0]);
  if (scaled >= 1000 && index < UNITS.length - 1) {
    index += 1;
    scaled = round2(abs / UNITS[index][0]);
  }
  return `${sign}$${scaled}${UNITS[index][1]}`;
}

/** The quote is one of its carrier's several options. */
export function hasCarrierOption(quote) {
  const number = quote && quote.optionNumber;
  return Number.isInteger(number) && number > 0;
}

/**
 * The carrier name, or for one of a carrier's several options
 * "Carrier – Option 1, $1M / $10K retention". A missing limit or retention is left
 * out, never shown as "$0" or "$null".
 */
export function carrierOptionLabel(carrierName, quote) {
  const name = carrierName === null || carrierName === undefined ? "" : String(carrierName);
  if (!hasCarrierOption(quote)) return name;
  const limit = formatOptionAmount(quote.optionLimit);
  const retention = formatOptionAmount(quote.optionRetention);
  const terms = [limit, retention ? `${retention} retention` : ""].filter(Boolean).join(" / ");
  const option = `Option ${quote.optionNumber}${terms ? `, ${terms}` : ""}`;
  return name ? `${name} – ${option}` : option;
}

/**
 * A file-name-safe form of the label, "Carrier Option 1": no "/" or "$". The carrier
 * name alone for a carrier with one option.
 */
export function carrierOptionFileLabel(carrierName, quote) {
  const name = carrierName === null || carrierName === undefined ? "" : String(carrierName);
  if (!hasCarrierOption(quote)) return name;
  return [name, `Option ${quote.optionNumber}`].filter(Boolean).join(" ");
}

/** The option fields an API row carries, or {} for a single-option carrier's row. */
export function pickOptionFields(row) {
  if (!hasCarrierOption(row)) return {};
  const out = {};
  for (const key of OPTION_FIELDS) out[key] = row[key] ?? null;
  return out;
}

/**
 * The list in its own order, except that a carrier's options sit next to each
 * other, in option-number order, where the carrier's first option appears. A list
 * with no options comes back in the same order.
 */
export function groupCarrierOptions(items, carrierKeyOf) {
  const list = Array.isArray(items) ? items : [];
  if (!list.some(hasCarrierOption)) return list;
  // An item with no carrier key is a group of its own, never merged with another.
  const keyOf = (item) => {
    const key = carrierKeyOf(item);
    return key === null || key === undefined || key === "" ? item : String(key);
  };
  const groups = new Map();
  for (const item of list) {
    if (!hasCarrierOption(item)) continue;
    const key = keyOf(item);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(item);
  }
  const out = [];
  const placed = new Set();
  for (const item of list) {
    if (!hasCarrierOption(item)) {
      out.push(item);
      continue;
    }
    const key = keyOf(item);
    if (placed.has(key)) continue;
    placed.add(key);
    out.push(...[...groups.get(key)].sort((a, b) => a.optionNumber - b.optionNumber));
  }
  return out;
}
